import { Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getInventoryOccupancy, getStructureVolumes, type DivergenceRow } from "../api/rest";
import { buildCoverageGrid, type CoverageCellStatus } from "./coverageGrid";
import { CollapsibleSection } from "../app/layout/CollapsibleSection";
import run from "./InventoryRunPage.module.css";
import css from "./CoverageGrid.module.css";

interface Props {
  siteId: string;
  warehouseId: string;
  runId: string;
  /** The page's own divergence rows — shared with the WMS section so the grid
   *  and the reconciliation table paint from the same fetch. */
  divRows: DivergenceRow[];
}

const STATUS_LABEL: Record<CoverageCellStatus, string> = {
  counted: "inventory.item.counted",
  empty: "inventory.item.empty",
  not_scanned: "inventory.item.not_scanned",
  unidentified: "inventory.item.unidentified",
  divergence: "inventory.coverageGrid.divergence",
};
const STATUSES: CoverageCellStatus[] = ["counted", "unidentified", "empty", "not_scanned", "divergence"];

/**
 * ADR 195: the coverage-grid report — the design's "per-aisle × position heatmap
 * grid". Skeleton from `structure/volumes`, paint from the run's occupancy cells
 * (the exact payload the 3D occupancy layer renders). Aisles collapse when there
 * are many (JSL: 13 aisles × 15k bins) so the page stays responsive; each opens
 * on demand.
 */
export function CoverageGridSection({ siteId, warehouseId, runId, divRows }: Props) {
  const { t } = useTranslation();
  const volumesQuery = useQuery({
    queryKey: ["structure-volumes", siteId, warehouseId],
    queryFn: () => getStructureVolumes(siteId, warehouseId),
  });
  const occQuery = useQuery({
    queryKey: ["inventory-occupancy-cells", siteId, runId],
    queryFn: () => getInventoryOccupancy(siteId, runId),
  });

  const volumes = volumesQuery.data?.volumes;
  const cells = occQuery.data?.locations;
  const grid = useMemo(
    () => buildCoverageGrid(volumes ?? [], cells ?? [], divRows),
    [volumes, cells, divRows],
  );

  if (grid.length === 0) return null; // structure-less warehouse: nothing to draw
  const openAll = grid.length <= 3;

  return (
    <CollapsibleSection
      id="run.coverageGrid"
      title={t("inventory.coverageGrid.title")}
      data-testid="coverage-grid-section"
    >
      <p className={run.note}>{t("inventory.coverageGrid.intro")}</p>
      <div className={css.legend}>
        {STATUSES.map((s) => (
          <span key={s} className={css.legendItem}>
            <i className={`${css.cell} ${css[s]}`} />
            {t(STATUS_LABEL[s])}
          </span>
        ))}
      </div>
      {grid.map((a) => (
        <details key={a.aisle} className={css.aisle} open={openAll} data-testid={`coverage-grid-${a.aisle}`}>
          <summary className={css.aisleSummary}>
            {a.aisle === "__none__" ? t("inventory.aisle.none") : a.aisle}
            <span className={css.aisleCounts}>
              {t("inventory.coverageGrid.aisleSummary", {
                bins: a.counts.counted + a.counts.empty + a.counts.not_scanned + a.counts.unidentified + a.counts.divergence,
                notScanned: a.counts.not_scanned,
                divergent: a.counts.divergence,
              })}
            </span>
          </summary>
          <div className={css.scroll}>
            <div
              className={css.grid}
              style={{ gridTemplateColumns: `auto repeat(${a.columns.length}, 14px)` }}
            >
              {a.levels.map((level, li) => (
                <Fragment key={level}>
                  <div className={css.levelLabel}>
                    {t("inventory.occupancy.level", { n: level })}
                  </div>
                  {a.columns.map((col) => {
                    const c = col.cells[li];
                    return c ? (
                      <div
                        key={`${col.key}:${level}`}
                        className={`${css.cell} ${css[c.status]}`}
                        title={`${c.address} — ${t(STATUS_LABEL[c.status])}`}
                      />
                    ) : (
                      <div key={`${col.key}:${level}`} className={css.hole} />
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>
        </details>
      ))}
    </CollapsibleSection>
  );
}
